import { FormControl, FormGroup } from '@angular/forms';
import { getIDType } from '@datorama/akita';
import { Y42EntityStore } from '@y42/state';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { endOfDay, startOfDay, sub } from 'date-fns';
import { JobStatusCode } from './job-status';
import { JobSortFilter, JobState } from './job.model';

const JOBS_MAX_DATE_RANGE = 7;

export const DefaultJobSearchFilter: Readonly<JobSortFilter> = {
  searchKey: '',
  statusFilter: null,
};

export abstract class JobStore<State extends JobState = JobState> extends Y42EntityStore<State> {
  constructor() {
    super({
      active: null,
      ui: {
        lastFetched: null,
        sortFilter: {
          global: {
            ...DefaultJobSearchFilter,
          },
        } as Record<string, JobSortFilter>,
      },
    } as State);
  }

  readonly maxDateRange = JOBS_MAX_DATE_RANGE;
  readonly selectedDateRangeForm = new FormGroup({
    from: new FormControl(startOfDay(sub(new Date(), { days: JOBS_MAX_DATE_RANGE - 1 })), { nonNullable: false }),
    to: new FormControl(endOfDay(new Date()), { nonNullable: false }),
  });

  setLastFetched() {
    this.updateUI({ lastFetched: new Date() });
  }

  setSearchKey(searchKey: string, context: string) {
    this.updateUI((uiState) => ({
      sortFilter: {
        ...uiState.sortFilter,
        [context]: {
          ...DefaultJobSearchFilter,
          ...uiState.sortFilter[context],
          searchKey,
        },
      },
    }));
  }

  setStatusFilter(statusFilter: JobStatusCode, context: string) {
    const currentStatusFilter = this.getValue().ui.sortFilter[context]?.statusFilter;
    this.updateUI((uiState) => ({
      sortFilter: {
        ...uiState.sortFilter,
        [context]: {
          ...DefaultJobSearchFilter,
          ...uiState.sortFilter[context],
          statusFilter: statusFilter === currentStatusFilter ? null : statusFilter,
        },
      },
    }));
  }

  delete(path: getIDType<State>): void {
    this.remove(path);
  }

  private updateUI(uiOrCallback: Partial<JobState> | ((ui: Partial<JobState>) => Partial<JobState>)) {
    if (typeof uiOrCallback === 'function') {
      return this.update((st) => {
        const ui = uiOrCallback(st.ui);
        return { ui: { ...st.ui, ...ui } } as State;
      });
    }
    return this.update((st) => ({ ui: { ...st.ui, ...uiOrCallback } } as State));
  }
}
